import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Paper,
  Stack,
  Chip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  AccountTree as TreeIcon,
} from '@mui/icons-material';

function DecisionTreeEditor({ model, onSave, onClose }) {
  const [tree, setTree] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [parentNode, setParentNode] = useState(null);
  const [nodeType, setNodeType] = useState('condition'); // 'condition' or 'leaf'
  const [editingNode, setEditingNode] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (model?.decision_tree) {
      setTree(JSON.parse(JSON.stringify(model.decision_tree))); // Deep copy
    }
  }, [model]);

  const handleNodeClick = (node, path = []) => {
    setSelectedNode({ node, path });
  };

  const handleEditNode = (node) => {
    setEditingNode(node);
    setEditDialogOpen(true);
  };

  const handleAddNode = (parent, asType) => {
    setParentNode(parent);
    setNodeType(asType);
    setEditingNode(null);
    setAddDialogOpen(true);
  };

  const handleSaveNode = () => {
    if (!editingNode) {
      // Adding new node
      const newNode = nodeType === 'leaf' 
        ? {
            type: 'leaf',
            outcome: '',
            message: '',
            missing_fields: []
          }
        : {
            type: 'condition',
            condition: '',
            true: { type: 'leaf', outcome: 'unknown' },
            false: { type: 'leaf', outcome: 'unknown' }
          };

      if (parentNode) {
        // Add to parent
        const newTree = JSON.parse(JSON.stringify(tree));
        const parent = findNodeInTree(newTree.root, parentNode);
        if (parent) {
          if (nodeType === 'condition') {
            // Replace a leaf with condition
            if (parent.type === 'leaf') {
              parent.type = 'condition';
              parent.condition = '';
              parent.true = newNode.true;
              parent.false = newNode.false;
            } else {
              // Add as child
              if (!parent.true || parent.true.type === 'leaf') {
                parent.true = newNode;
              } else if (!parent.false || parent.false.type === 'leaf') {
                parent.false = newNode;
              }
            }
          } else {
            // Add leaf
            if (parent.type === 'condition') {
              if (!parent.true) parent.true = newNode;
              else if (!parent.false) parent.false = newNode;
            }
          }
        }
        setTree(newTree);
      } else {
        // Root node
        setTree({ root: newNode });
      }
    } else {
      // Editing existing node
      const newTree = JSON.parse(JSON.stringify(tree));
      const nodeToEdit = findNodeInTree(newTree.root, editingNode);
      if (nodeToEdit) {
        Object.assign(nodeToEdit, editingNode);
      }
      setTree(newTree);
    }
    setEditDialogOpen(false);
    setAddDialogOpen(false);
    setEditingNode(null);
    setParentNode(null);
  };

  const handleDeleteNode = (node) => {
    if (!window.confirm('Are you sure you want to delete this node?')) return;
    
    const newTree = JSON.parse(JSON.stringify(tree));
    // If deleting root, reset to empty
    if (node === newTree.root) {
      setTree({ root: { type: 'leaf', outcome: 'unknown' } });
    } else {
      // Find and remove from parent
      removeNodeFromTree(newTree.root, node);
      setTree(newTree);
    }
  };

  const findNodeInTree = (current, target) => {
    if (current === target) return current;
    if (current.type === 'condition') {
      if (current.true) {
        const found = findNodeInTree(current.true, target);
        if (found) return found;
      }
      if (current.false) {
        const found = findNodeInTree(current.false, target);
        if (found) return found;
      }
    }
    return null;
  };

  const removeNodeFromTree = (current, target) => {
    if (current.type === 'condition') {
      if (current.true === target) {
        current.true = { type: 'leaf', outcome: 'unknown' };
        return true;
      }
      if (current.false === target) {
        current.false = { type: 'leaf', outcome: 'unknown' };
        return true;
      }
      if (current.true && removeNodeFromTree(current.true, target)) return true;
      if (current.false && removeNodeFromTree(current.false, target)) return true;
    }
    return false;
  };

  const handleSaveTree = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await fetch(`/api/v1/policy/models/${model.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision_tree: tree }),
      });

      if (response.ok) {
        if (onSave) onSave();
        if (onClose) onClose();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to save tree');
      }
    } catch (err) {
      setError(`Error saving tree: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const renderNode = (node, depth = 0, path = []) => {
    if (!node) return null;

    const isSelected = selectedNode?.node === node;
    const indent = depth * 40;

    if (node.type === 'leaf') {
      return (
        <Box key={path.join('-')} sx={{ ml: indent, mb: 2 }}>
          <Paper
            sx={{
              p: 2,
              bgcolor: isSelected ? 'primary.light' : 'background.paper',
              border: 2,
              borderColor: isSelected ? 'primary.main' : 'divider',
              borderRadius: 2,
              cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main' },
            }}
            onClick={() => handleNodeClick(node, path)}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Chip label="Leaf" size="small" color="success" sx={{ mr: 1 }} />
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Outcome: {node.outcome || 'unknown'}
                </Typography>
                {node.message && (
                  <Typography variant="body2" color="text.secondary">
                    {node.message}
                  </Typography>
                )}
                {node.missing_fields && node.missing_fields.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Missing: {node.missing_fields.join(', ')}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Stack direction="row" spacing={1}>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditNode(node); }}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleAddNode(node, 'condition'); }}>
                  <AddIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteNode(node); }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
          </Paper>
        </Box>
      );
    }

    if (node.type === 'condition') {
      return (
        <Box key={path.join('-')} sx={{ ml: indent, mb: 2 }}>
          <Paper
            sx={{
              p: 2,
              bgcolor: isSelected ? 'primary.light' : 'background.paper',
              border: 2,
              borderColor: isSelected ? 'primary.main' : 'divider',
              borderRadius: 2,
              cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main' },
            }}
            onClick={() => handleNodeClick(node, path)}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Chip label="Condition" size="small" color="primary" sx={{ mr: 1 }} />
                <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                  {node.condition || '(no condition)'}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditNode(node); }}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleAddNode(node, 'condition'); }}>
                  <AddIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteNode(node); }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
            <Box sx={{ pl: 2, borderLeft: 2, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                TRUE:
              </Typography>
              {renderNode(node.true, depth + 1, [...path, 'true'])}
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', mt: 2 }}>
                FALSE:
              </Typography>
              {renderNode(node.false, depth + 1, [...path, 'false'])}
            </Box>
          </Paper>
        </Box>
      );
    }

    return null;
  };

  if (!tree || !tree.root) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No decision tree available</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          <TreeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Decision Tree Editor
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleAddNode(null, 'condition')}
          >
            Add Root Node
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveTree}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <CheckIcon />}
          >
            {saving ? 'Saving...' : 'Save Tree'}
          </Button>
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ borderRadius: 3, maxHeight: '70vh', overflow: 'auto' }}>
        <CardContent>
          {renderNode(tree.root, 0, ['root'])}
        </CardContent>
      </Card>

      {/* Edit/Add Node Dialog */}
      <Dialog
        open={editDialogOpen || addDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setAddDialogOpen(false);
          setEditingNode(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editDialogOpen ? 'Edit Node' : `Add ${nodeType === 'leaf' ? 'Leaf' : 'Condition'} Node`}
        </DialogTitle>
        <DialogContent>
          <NodeEditorForm
            node={editingNode || (nodeType === 'leaf' ? { type: 'leaf', outcome: '', message: '', missing_fields: [] } : { type: 'condition', condition: '' })}
            nodeType={editingNode?.type || nodeType}
            onChange={(updated) => setEditingNode(updated)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditDialogOpen(false);
            setAddDialogOpen(false);
            setEditingNode(null);
          }}>
            Cancel
          </Button>
          <Button onClick={handleSaveNode} variant="contained">
            {editDialogOpen ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function NodeEditorForm({ node, nodeType, onChange }) {
  const [localNode, setLocalNode] = useState(node);

  useEffect(() => {
    setLocalNode(node);
  }, [node]);

  const updateField = (field, value) => {
    const updated = { ...localNode, [field]: value };
    setLocalNode(updated);
    if (onChange) onChange(updated);
  };

  if (nodeType === 'leaf') {
    return (
      <Stack spacing={3} sx={{ mt: 1 }}>
        <TextField
          fullWidth
          label="Outcome"
          value={localNode.outcome || ''}
          onChange={(e) => updateField('outcome', e.target.value)}
          placeholder="e.g., Residential Permit"
        />
        <TextField
          fullWidth
          label="Message"
          value={localNode.message || ''}
          onChange={(e) => updateField('message', e.target.value)}
          multiline
          rows={2}
        />
        <TextField
          fullWidth
          label="Missing Fields (comma-separated)"
          value={Array.isArray(localNode.missing_fields) ? localNode.missing_fields.join(', ') : ''}
          onChange={(e) => {
            const fields = e.target.value.split(',').map(f => f.trim()).filter(f => f);
            updateField('missing_fields', fields);
          }}
          placeholder="field1, field2, field3"
        />
      </Stack>
    );
  }

  if (nodeType === 'condition') {
    return (
      <Stack spacing={3} sx={{ mt: 1 }}>
        <TextField
          fullWidth
          label="Condition"
          value={localNode.condition || ''}
          onChange={(e) => updateField('condition', e.target.value)}
          placeholder="e.g., property_type == 'residential'"
          helperText="Examples: field == 'value', field > 100, exists:field"
          multiline
          rows={3}
        />
        <Alert severity="info">
          When you add this condition, it will create TRUE and FALSE branches. You can edit those after adding.
        </Alert>
      </Stack>
    );
  }

  return null;
}

export default DecisionTreeEditor;

